import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/admin");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-ink flex flex-col" data-testid="admin-login-page">
      <div className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-ink-border">
        <Link to="/" className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/80">
          ← Back to site
        </Link>
        <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/50">
          Admin access
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <h1 className="font-oswald uppercase tracking-tight text-5xl md:text-6xl text-bone leading-none">
            Sign in
          </h1>
          <p className="mt-3 font-cormorant italic text-xl text-bone/70">
            Manage the portfolio.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="block font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/60 mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-ink-surface border border-ink-border text-bone px-4 py-3 font-manrope text-sm focus:outline-none focus:border-bone transition-colors"
                placeholder="samuel@samoso.com"
              />
            </div>

            <div>
              <label className="block font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/60 mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-ink-surface border border-ink-border text-bone px-4 py-3 font-manrope text-sm focus:outline-none focus:border-bone transition-colors"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="font-manrope text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-4 py-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-bone text-ink font-oswald uppercase tracking-tight text-lg py-3 hover:bg-bone/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
